import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

export function Reveal({ children, delay = 0, y = 28, className = "", ...rest }) {
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, delay, ease: EASE }}
            {...rest}
        >
            {children}
        </motion.div>
    );
}

export function MaskedLines({ lines, as = "h1", className = "", lineClassName = "", delay = 0.15, stagger = 0.14 }) {
    const Tag = motion[as] || motion.h1;
    return (
        <Tag className={className} initial="hidden" animate="show">
            {lines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
                    <motion.span
                        className={`block ${lineClassName}`}
                        variants={{ hidden: { y: "115%" }, show: { y: "0%" } }}
                        transition={{ duration: 1.05, delay: delay + i * stagger, ease: EASE }}
                    >
                        {line}
                    </motion.span>
                </span>
            ))}
        </Tag>
    );
}

export function Eyebrow({ children, className = "" }) {
    return (
        <div
            className={`flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] uppercase text-copper font-medium ${className}`}
            data-testid="section-eyebrow"
        >
            <span className="h-px w-10 bg-copper/60" />
            {children}
        </div>
    );
}

export function CountUp({ value, decimals = 0, suffix = "", prefix = "", className = "" }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.4 });
    const fmt = (v) =>
        v.toLocaleString("en-US", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    /* Show the real number immediately — animation only enhances it once in view. */
    const [display, setDisplay] = useState(fmt(value));

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, value, {
            duration: 2.2,
            ease: EASE,
            onUpdate: (v) => setDisplay(fmt(v)),
        });
        return () => controls.stop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, value, decimals]);

    return (
        <span ref={ref} className={className}>
            {prefix}
            {display}
            {suffix}
        </span>
    );
}

export function Logo({ size = 32 }) {
    return (
        <img
            src="/brand/roxy-emblem.png"
            width={size}
            height={size}
            alt="Roxy Cables logo"
            data-testid="logo-mark"
            className="rounded-sm object-cover"
            style={{ height: size, width: "auto" }}
        />
    );
}
